/**
 * Pure helpers for tiptap (ProseMirror) scene documents.
 *
 * These operate on plain ProseMirror doc JSON (the parsed `scene.content`) and
 * deliberately import nothing from tiptap/prosemirror, so they can run in jest
 * (which does not transform the ESM tiptap packages) and be unit tested. The
 * decoration entry point also accepts a live ProseMirror Node and normalizes it
 * to JSON before using the pure helpers.
 *
 * Comment anchoring for 'tiptap'-format books mirrors the server
 * (api/src/utils/tiptap/tiptapContent.ts and models/comment/inlineComment.js):
 * an anchor is {sceneId, startKey, startOffset, endKey, endOffset} where
 * startKey/endKey are stable block ids (the `pid` node attribute) and the
 * offsets are character offsets within that block's text — the direct analog of
 * Draft's (blockKey, offset). For a code block the text is the raw code, so a
 * newline is one character like any other and offsets stay linear.
 */

// Block node types that carry a pid, i.e. the blocks an inline comment can be
// anchored in (must match the server's PID_TYPES).
//
// `codeBlock` is here so readers can comment inside fenced code — a Markdown/
// Leanpub manuscript is largely code, YAML and ASCII diagrams, and without a
// pid `rangeToAnchor` returns null for any selection in a <pre> and the
// reader's selection toolbar never opens.
export const PID_TYPES = ['paragraph', 'heading', 'codeBlock'];

// Block node types that count as prose for the chapter's text/word/char/
// paragraph counts (must match the server's COUNT_TYPES).
//
// Deliberately NOT the same list as PID_TYPES: code blocks are anchorable but
// are not counted, so adding them above does not silently inflate word counts
// and reading-time estimates — and does not make a re-saved scene disagree with
// every scene saved before this change.
const COUNT_TYPES = ['paragraph', 'heading'];

// Nodes with no children that still occupy a single ProseMirror position.
const LEAF_TYPES = new Set([
  'image',
  'horizontalRule',
  'horizontal_rule',
  'hardBreak',
  'hard_break',
]);

/** ProseMirror node size of a JSON node (matches Node.nodeSize semantics). */
export function nodeSize(node) {
  if (!node) return 0;
  if (node.type === 'text') return (node.text || '').length;
  if (LEAF_TYPES.has(node.type)) return 1;
  const content = Array.isArray(node.content) ? node.content : [];
  let sum = 0;
  for (const child of content) sum += nodeSize(child);
  // opening + closing tokens surround a block node's content
  return sum + 2;
}

function nodeText(node) {
  if (!node) return '';
  if (node.type === 'text') return node.text || '';
  return (node.content || []).map(nodeText).join('');
}

/** Depth-first walk over every counted prose block (including nested ones). */
function walkCountBlocks(node, fn) {
  for (const child of node.content || []) {
    if (COUNT_TYPES.includes(child.type)) fn(child);
    if (Array.isArray(child.content) && !LEAF_TYPES.has(child.type)) {
      walkCountBlocks(child, fn);
    }
  }
}

/**
 * Collect { pid, contentStart, contentEnd, textLength } for every pid block,
 * where contentStart is the absolute ProseMirror position just inside the
 * block (before its first character). offset 0 of an anchor maps to
 * contentStart.
 */
export function collectPidBlocks(doc) {
  const blocks = [];
  const walk = (node, contentStartPos) => {
    let pos = contentStartPos;
    for (const child of node.content || []) {
      const childOpenPos = pos;
      const childContentStart = childOpenPos + 1;
      const size = nodeSize(child);
      const childContentEnd = childOpenPos + size - 1;
      if (PID_TYPES.includes(child.type) && child.attrs && child.attrs.pid) {
        blocks.push({
          pid: child.attrs.pid,
          contentStart: childContentStart,
          contentEnd: childContentEnd,
          textLength: childContentEnd - childContentStart,
        });
      }
      if (Array.isArray(child.content) && !LEAF_TYPES.has(child.type)) {
        walk(child, childContentStart);
      }
      pos += size;
    }
  };
  walk(doc, 0);
  return blocks;
}

/**
 * Map an inline-comment anchor to a { from, to } ProseMirror range inside the
 * given scene doc, or null when a referenced block no longer exists (an
 * orphaned comment — the same "InlineCommentNotFound" case the Draft path has).
 */
export function anchorToRange(doc, anchor) {
  if (!doc || !anchor) return null;
  const blocks = collectPidBlocks(doc);
  const start = blocks.find(b => b.pid === anchor.startKey);
  if (!start) return null;
  const end =
    anchor.endKey === anchor.startKey
      ? start
      : blocks.find(b => b.pid === anchor.endKey);
  if (!end) return null;
  const from =
    start.contentStart + clamp(anchor.startOffset, 0, start.textLength);
  const to = end.contentStart + clamp(anchor.endOffset, 0, end.textLength);
  if (to <= from) return null;
  return { from, to };
}

/**
 * Inverse of anchorToRange: given a ProseMirror selection range, produce the
 * stored anchor shape {startKey, startOffset, endKey, endOffset} (pids +
 * offsets). Mirrors how reactionsComponent.js reads a Draft SelectionState.
 */
export function rangeToAnchor(doc, from, to) {
  const blocks = collectPidBlocks(doc);
  const startBlock = blocks.find(
    b => from >= b.contentStart && from <= b.contentEnd
  );
  if (!startBlock) return null;
  const endBlock =
    blocks.find(b => to >= b.contentStart && to <= b.contentEnd) || startBlock;
  return {
    startKey: startBlock.pid,
    startOffset: from - startBlock.contentStart,
    endKey: endBlock.pid,
    endOffset: clamp(to - endBlock.contentStart, 0, endBlock.textLength),
  };
}

/** Build the shared audiobook feedback-dock payload from a TipTap range. */
export function audioFeedbackSelectionFromRange(doc, sceneId, from, to, text) {
  if (!doc || from === to || !text) return null;
  const anchor = rangeToAnchor(doc, from, to);
  if (!anchor) return null;
  return {
    text,
    anchor: { sceneId, ...anchor },
  };
}

/**
 * Turn a list of comments into decoration specs [{ from, to, comment }] for one
 * scene. Comments whose inlineInfo.sceneId names a different scene are skipped;
 * orphaned anchors (block deleted) are dropped.
 */
export function commentsToDecorationSpecs(doc, comments, sceneId) {
  let jsonDoc = doc;
  let didNormalizeDoc = false;
  const specs = [];
  for (const comment of comments || []) {
    const info = comment && comment.inlineInfo;
    if (!info) continue;
    if (info.sceneId && sceneId && info.sceneId !== sceneId) continue;
    if (!didNormalizeDoc) {
      jsonDoc = doc && typeof doc.toJSON === 'function' ? doc.toJSON() : doc;
      didNormalizeDoc = true;
    }
    const range = anchorToRange(jsonDoc, info);
    if (!range) continue;
    specs.push({ from: range.from, to: range.to, comment });
  }
  return specs;
}

/** Resolve the transient audio playhead for one scene without mutating text. */
export function audioHighlightToDecorationSpec(doc, anchor, sceneId) {
  if (!anchor) return null;
  if (anchor.sceneId && sceneId && anchor.sceneId !== sceneId) return null;
  const jsonDoc = doc && typeof doc.toJSON === 'function' ? doc.toJSON() : doc;
  return anchorToRange(jsonDoc, anchor);
}

/**
 * Chapter/scene count fields, mirroring the server's countFields so client and
 * server agree: blocks joined by "\n", words by whitespace, paragraphs =
 * non-empty COUNT_TYPES blocks (prose only — code blocks are excluded). Also
 * returns the plain text (for chapter `beginning`).
 */
export function computeCounts(doc) {
  const texts = [];
  walkCountBlocks(doc, n => texts.push(nodeText(n)));
  const text = texts.join('\n');
  return {
    text,
    wordCount: text.split(/\s+/).filter(Boolean).length,
    charCount: text.length,
    paragraphCount: texts.filter(t => t.trim().length > 0).length,
  };
}

function clamp(value, min, max) {
  const n = typeof value === 'number' ? value : 0;
  if (n < min) return min;
  if (n > max) return max;
  return n;
}
